import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Handshake, Plus, Upload, Send, X, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  submitted:         '#fbbf24',
  accepted:          '#60a5fa',
  awaiting_shipment: '#60a5fa',
  in_transit:        '#38bdf8',
  received:          '#c084fc',
  listed:            '#2DD4BF',
  sold:              '#a855f7',
  needs_review:      '#fb923c',
  rejected:          '#ef4444',
  returned:          '#71717a',
  expired:           '#71717a',
};

export default function MyConsignments() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ items: [], wallet: null, stats: null, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [showLedger, setShowLedger] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/consignment/my`, { headers: getAuthHeader() });
      setData(r.data || {});
    } catch {
      toast.error('FAILED TO LOAD');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const wallet = data.wallet || { balance: 0, total_earned: 0, total_paid_out: 0, items_sold_count: 0 };
  const stats = data.stats || { by_status: {}, total_sold: 0, total_revenue: 0, total_earnings: 0 };

  const pieSegments = useMemo(() => {
    const entries = Object.entries(stats.by_status || {}).filter(([_, v]) => v > 0);
    const total = entries.reduce((a, [_, v]) => a + v, 0);
    if (!total) return [];
    let acc = 0;
    return entries.map(([k, v]) => {
      const pct = (v / total) * 100;
      const start = acc;
      acc += pct;
      return { key: k, count: v, pct, start, end: acc, color: STATUS_COLORS[k] || '#888' };
    });
  }, [stats.by_status]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="p-2 hover:bg-zinc-900 shrink-0"><ArrowLeft size={18} /></Link>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-bold font-mono tracking-widest flex items-center gap-2"><Handshake size={16} /> CONSIGNMENT</h1>
              <p className="text-[10px] sm:text-xs text-zinc-500 font-mono truncate">YOUR ITEMS · EARNINGS · PAYOUTS</p>
            </div>
          </div>
          <button onClick={() => setShowSubmit(true)} className="px-3 sm:px-4 py-2 bg-tiffany-500 text-black font-mono text-[10px] sm:text-xs tracking-widest hover:bg-tiffany-400 flex items-center gap-1.5 shrink-0" data-testid="open-submit-btn">
            <Plus size={12} /> <span className="hidden xs:inline">SUBMIT </span>ITEM
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Wallet card */}
        <div className="border border-tiffany-500/40 bg-zinc-950 p-4 sm:p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[10px] text-zinc-500 tracking-widest">CONSIGNOR BALANCE</p>
              <p className="font-mono text-2xl sm:text-3xl font-bold text-tiffany-500" data-testid="consignor-balance">${wallet.balance?.toFixed(2)}</p>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">USE AT CHECKOUT OR REQUEST PAYOUT</p>
            </div>
            <button
              onClick={() => setShowPayout(true)}
              disabled={!wallet.balance || wallet.balance <= 0}
              className="px-3 sm:px-4 py-2 border border-tiffany-500/40 text-tiffany-400 font-mono text-[10px] sm:text-xs tracking-widest hover:bg-tiffany-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="request-payout-btn"
            >
              <Send size={12} /> REQUEST PAYOUT
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-zinc-800">
            <Stat label="EARNED"   value={`$${wallet.total_earned?.toFixed(0) || 0}`}     color="text-tiffany-500" />
            <Stat label="PAID OUT" value={`$${wallet.total_paid_out?.toFixed(0) || 0}`}  color="text-purple-400" />
            <Stat label="SOLD"     value={wallet.items_sold_count || 0}                  color="text-zinc-300" />
          </div>
        </div>

        {/* Stats / pie */}
        {pieSegments.length > 0 && (
          <div className="border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
            <p className="font-mono text-[10px] text-zinc-500 tracking-widest mb-3 sm:mb-4">YOUR ITEMS · BY STATUS</p>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
              <ConicPie segments={pieSegments} />
              <div className="space-y-1.5 flex-1 min-w-[140px]">
                {pieSegments.map(s => (
                  <div key={s.key} className="flex items-center gap-2 font-mono text-[11px] sm:text-xs">
                    <div className="w-3 h-3 shrink-0" style={{ background: s.color }} />
                    <span className="text-white uppercase">{s.key.replace('_', ' ')}</span>
                    <span className="text-zinc-500 ml-auto">{s.count} · {s.pct.toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Items list */}
        <div className="space-y-3">
          <p className="font-mono text-[10px] text-zinc-500 tracking-widest">YOUR SUBMISSIONS · {data.items?.length || 0}</p>
          {loading ? <p className="text-center py-12 font-mono text-zinc-500">LOADING_</p>
          : !data.items?.length ? (
            <div className="border border-zinc-800 p-6 sm:p-8 text-center">
              <Handshake className="mx-auto mb-3 text-zinc-700" size={32} />
              <p className="font-mono text-zinc-500 text-xs tracking-wider mb-3">NO CONSIGNMENT SUBMISSIONS YET</p>
              <button onClick={() => setShowSubmit(true)} className="font-mono text-xs text-tiffany-500 hover:underline">+ SUBMIT YOUR FIRST ITEM</button>
            </div>
          ) : (
            data.items.map(item => (
              <UserConsignmentItem key={item.id} item={item} onAction={load} />
            ))
          )}
        </div>

        {/* Ledger */}
        {data.transactions?.length > 0 && (
          <div className="border border-zinc-800 bg-zinc-950">
            <button onClick={() => setShowLedger(!showLedger)} className="w-full p-4 flex items-center justify-between font-mono text-xs text-zinc-400 tracking-widest hover:text-white">
              <span>TRANSACTION LEDGER · {data.transactions.length}</span>
              {showLedger ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showLedger && (
              <div className="border-t border-zinc-800 max-h-72 overflow-y-auto">
                {data.transactions.map(t => (
                  <div key={t.id} className="px-4 py-2 border-b border-zinc-900 flex items-center justify-between text-[11px] font-mono gap-3">
                    <div className="min-w-0 flex-1">
                      <p className={`tracking-widest ${t.amount >= 0 ? 'text-tiffany-400' : 'text-purple-400'}`}>{t.type.toUpperCase()}</p>
                      <p className="text-zinc-500 truncate">{t.note}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={t.amount >= 0 ? 'text-tiffany-400' : 'text-purple-400'}>{t.amount >= 0 ? '+' : ''}${t.amount.toFixed(2)}</p>
                      <p className="text-zinc-600 text-[10px]">{new Date(t.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showSubmit && <SubmitModal onClose={() => setShowSubmit(false)} onSaved={() => { setShowSubmit(false); load(); }} />}
      {showPayout && <PayoutModal balance={wallet.balance} onClose={() => setShowPayout(false)} onSaved={() => { setShowPayout(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <p className={`font-mono text-base font-bold ${color}`}>{value}</p>
      <p className="font-mono text-[9px] text-zinc-600 tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function ConicPie({ segments }) {
  const grad = segments.map(s => `${s.color} ${s.start}% ${s.end}%`).join(', ');
  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0" data-testid="consignor-pie">
      <div className="w-full h-full rounded-full" style={{ background: `conic-gradient(${grad})` }} />
      <div className="absolute inset-3 rounded-full bg-zinc-950 flex items-center justify-center">
        <p className="font-mono text-lg sm:text-xl font-bold text-white">{segments.reduce((a, s) => a + s.count, 0)}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SUBMIT MODAL — mirrors TradeOfferModal styling
// ---------------------------------------------------------------------------
function SubmitModal({ onClose, onSaved }) {
  const { getAuthHeader } = useAuth();

  // Item count (1-10). We pre-allocate a row per slot so the user can fill each one.
  const [itemCount, setItemCount] = useState(1);
  const blankItem = () => ({ title: '', description: '', brand: '', category: '', suggested_price: '', photos: [] });
  const [items, setItems] = useState([blankItem()]);
  const [payoutPref, setPayoutPref] = useState('credit');
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Fetch brand + category options
  useEffect(() => {
    (async () => {
      try {
        const [bRes, cRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`).then(r => r.ok ? r.json() : []),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/categories`).then(r => r.ok ? r.json() : []),
        ]);
        setBrands(Array.isArray(bRes) ? bRes : []);
        setCategories(Array.isArray(cRes) ? cRes : []);
      } catch {/* silent */}
    })();
  }, []);

  // Grow / shrink items array when count changes
  const changeCount = (n) => {
    const next = Math.max(1, Math.min(10, parseInt(n, 10) || 1));
    setItemCount(next);
    setItems((prev) => {
      if (prev.length === next) return prev;
      if (prev.length < next) return [...prev, ...Array.from({ length: next - prev.length }, () => blankItem())];
      return prev.slice(0, next);
    });
  };

  const setItem = (idx, k, v) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));

  const uploadPhoto = async (idx, filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const cur = items[idx].photos || [];
    if (cur.length + files.length > 6) {
      setError(`MAX 6 PHOTOS PER ITEM (ITEM ${idx + 1})`);
      return;
    }
    setUploadingIdx(idx);
    setError('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload/user-image`, fd, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        });
        setItems((prev) => prev.map((it, i) => i === idx ? { ...it, photos: [...it.photos, r.data.url] } : it));
      }
    } catch (err) {
      setError((err?.response?.data?.detail || 'UPLOAD FAILED').toUpperCase());
    } finally {
      setUploadingIdx(null);
    }
  };

  const save = async () => {
    setError('');
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.title.trim())                                          { setError(`ITEM ${i + 1} · TITLE REQUIRED`);          return; }
      if (!it.description.trim())                                    { setError(`ITEM ${i + 1} · DESCRIPTION REQUIRED`);    return; }
      if (!it.brand)                                                 { setError(`ITEM ${i + 1} · BRAND REQUIRED`);          return; }
      if (!it.category)                                              { setError(`ITEM ${i + 1} · CATEGORY REQUIRED`);       return; }
      if (!it.suggested_price || parseFloat(it.suggested_price) <= 0){ setError(`ITEM ${i + 1} · VALID SUGGESTED PRICE`);   return; }
      if (it.photos.length === 0)                                    { setError(`ITEM ${i + 1} · AT LEAST ONE PHOTO`);      return; }
    }
    if (!agreed) { setError('PLEASE READ AND ACCEPT THE CONSIGNMENT TERMS'); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/consignment/submit-batch`, {
        payout_preference: payoutPref,
        items: items.map(it => ({
          title: it.title,
          description: it.description,
          brand: it.brand,
          category: it.category,
          suggested_price: parseFloat(it.suggested_price),
          photos: it.photos,
        })),
      }, { headers: getAuthHeader() });
      setSuccess(`SUBMITTED ${items.length} ITEM(S)! WE WILL REVIEW AND GET BACK TO YOU.`);
      setTimeout(() => { onSaved(); }, 1500);
    } catch (e) {
      setError((e?.response?.data?.detail || 'SUBMIT FAILED').toString().toUpperCase());
    } finally { setSaving(false); }
  };

  const imageUrl = (p) => p?.startsWith('http') ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`;
  const removePhoto = (idx, photoIdx) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, photos: it.photos.filter((_, j) => j !== photoIdx) } : it));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">SUBMIT FOR CONSIGNMENT</h2>
            <p className="text-xs text-zinc-500 font-mono">SUBMIT YOUR ITEMS FOR CONSIGNMENT BELOW.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm font-mono">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-3 py-2 rounded-lg text-sm font-mono">
              {success}
            </div>
          )}

          {/* Item count selector */}
          <div className="border border-zinc-700 rounded-lg p-4 space-y-3">
            <p className="text-sm text-zinc-400 flex items-center gap-2 font-mono">
              <ArrowRight size={14} />
              HOW MANY ITEMS ARE YOU CONSIGNING?
            </p>
            <Dropdown
              value={String(itemCount)}
              onChange={(v) => changeCount(v)}
              options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} ITEM${i === 0 ? '' : 'S'}` }))}
              placeholder="1 ITEM"
              testId="cons-item-count"
            />
          </div>

          {/* Per-item forms */}
          {items.map((it, idx) => (
            <div key={idx} className="border border-zinc-700 rounded-lg p-4 space-y-3" data-testid={`cons-item-${idx}`}>
              <p className="text-sm text-tiffany-400 flex items-center gap-2 font-mono">
                <ArrowRight size={14} />
                ITEM {idx + 1}
              </p>

              <input
                type="text"
                value={it.title}
                onChange={(e) => setItem(idx, 'title', e.target.value.toUpperCase())}
                placeholder={`ITEM ${idx + 1} TITLE *`}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono uppercase"
                data-testid={`cons-title-${idx}`}
              />

              <textarea
                value={it.description}
                onChange={(e) => setItem(idx, 'description', e.target.value.toUpperCase())}
                placeholder="BRIEF DESCRIPTION — CONDITION, FLAWS... *"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
                data-testid={`cons-desc-${idx}`}
              />

              <div className="grid grid-cols-2 gap-3">
                <Dropdown
                  value={it.brand}
                  onChange={(v) => setItem(idx, 'brand', v)}
                  placeholder="BRAND *"
                  options={brands.map(b => ({ value: b.name || b, label: (b.name || b).toUpperCase() }))}
                  testId={`cons-brand-${idx}`}
                  required
                />
                <Dropdown
                  value={it.category}
                  onChange={(v) => setItem(idx, 'category', v)}
                  placeholder="CATEGORY *"
                  options={categories.map(c => ({ value: c, label: c.toUpperCase() }))}
                  testId={`cons-cat-${idx}`}
                  required
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1 font-mono">SUGGESTED PRICE ($) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={it.suggested_price}
                  onChange={(e) => setItem(idx, 'suggested_price', e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
                  data-testid={`cons-price-${idx}`}
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1 font-mono">PHOTOS · ITEM {idx + 1} *</label>
                {it.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {it.photos.map((p, j) => (
                      <div key={j} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                        <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx, j)}
                          className="absolute top-1 right-1 p-0.5 bg-black/70 rounded-full hover:bg-red-500"
                          data-testid={`remove-photo-${idx}-${j}`}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {it.photos.length < 6 && (
                  <label className={`block border border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${it.photos.length === 0 ? 'border-red-500/50 hover:border-red-500' : 'border-zinc-700 hover:border-teal-500'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => uploadPhoto(idx, e.target.files)}
                      className="hidden"
                      disabled={uploadingIdx !== null}
                      data-testid={`cons-photo-upload-${idx}`}
                    />
                    {uploadingIdx === idx ? (
                      <span className="text-sm text-teal-400 font-mono flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></span>
                        UPLOADING...
                      </span>
                    ) : (
                      <span className={`text-sm flex items-center justify-center gap-2 font-mono ${it.photos.length === 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                        <Upload size={14} /> {it.photos.length === 0 ? 'ADD PHOTOS (REQUIRED)' : 'ADD MORE PHOTOS'}
                      </span>
                    )}
                  </label>
                )}
              </div>
            </div>
          ))}

          {/* Payout Preference */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">PAYOUT PREFERENCE *</label>
            <Dropdown
              value={payoutPref}
              onChange={setPayoutPref}
              options={[
                { value: 'credit', label: 'STORE CREDIT · INSTANT' },
                { value: 'cash',   label: 'CASH VIA PAYPAL' },
              ]}
              testId="cons-payout-pref"
            />
            <p className="text-[10px] text-zinc-600 mt-1 font-mono">YOU CAN SWITCH THIS LATER FROM YOUR PROFILE.</p>
          </div>

          {/* TERMS */}
          <div className="bg-zinc-800/50 border border-zinc-700 p-3">
            <p className="text-[10px] text-tiffany-400 font-mono tracking-widest mb-2">CONSIGNMENT TERMS</p>
            <ul className="text-[10px] text-zinc-400 font-mono leading-relaxed space-y-1 list-disc pl-4">
              <li>WE MUST PHYSICALLY RECEIVE AND INSPECT YOUR ITEM(S) BEFORE THEY ARE LISTED.</li>
              <li>BLK MRKT HANDLES ALL SHIPPING TO THE BUYER ONCE AN ITEM SELLS.</li>
              <li>DEFAULT EARNINGS SPLIT IS 70% TO YOU / 30% TO BLK MRKT — CONFIRMED WHEN WE ACCEPT THE LISTING.</li>
              <li>IF WE PROPOSE DIFFERENT TERMS, YOU CAN ACCEPT, COUNTER, OR DECLINE BEFORE WE LIST.</li>
              <li>IF YOUR ITEM HAS NOT SOLD AFTER 45 DAYS, WE WILL REASSESS — RE-LIST, ADJUST PRICE, OR ARRANGE RETURN.</li>
              <li><strong>RETURN SHIPPING IS AT THE SELLER'S COST.</strong></li>
              <li>YOUR EARNINGS GO TO YOUR BLK MRKT CONSIGNOR BALANCE — USABLE INSTANTLY ON SITE OR VIA CASH PAYOUT.</li>
              <li>ALL CONSIGNMENTS ARE SUBJECT TO INSPECTION AND APPROVAL.</li>
            </ul>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="cons-agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-teal-500 cursor-pointer shrink-0"
              data-testid="cons-agree-checkbox"
            />
            <span className="text-[11px] font-mono text-zinc-300 leading-relaxed">
              I HAVE READ AND ACCEPT THE CONSIGNMENT TERMS ABOVE.
            </span>
          </label>

          <button
            onClick={save}
            disabled={saving || uploadingIdx !== null || !!success}
            className="w-full py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-lg transition-colors font-mono flex items-center justify-center gap-2"
            data-testid="submit-cons-btn"
          >
            {(uploadingIdx !== null || saving) && (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            )}
            {uploadingIdx !== null ? 'UPLOADING PHOTOS...' : saving ? 'SUBMITTING...' : `SUBMIT ${itemCount} ITEM${itemCount === 1 ? '' : 'S'} FOR CONSIGNMENT`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PAYOUT MODAL — same styling
// ---------------------------------------------------------------------------
function PayoutModal({ balance, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [amount, setAmount] = useState(balance);
  const [method, setMethod] = useState('paypal');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async () => {
    setError('');
    const amt = parseFloat(amount);
    if (!amt || amt <= 0)   { setError('PLEASE ENTER A VALID AMOUNT'); return; }
    if (amt > balance)      { setError(`MAX AVAILABLE IS $${balance.toFixed(2)}`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/consignment/payout-request`, { amount: amt, method, note }, { headers: getAuthHeader() });
      toast.success('PAYOUT REQUESTED');
      onSaved();
    } catch (e) {
      setError((e?.response?.data?.detail || 'REQUEST FAILED').toString().toUpperCase());
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md">
        <div className="border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">REQUEST PAYOUT</h2>
            <p className="text-xs text-zinc-500 font-mono">AVAILABLE · <span className="text-teal-400">${balance.toFixed(2)}</span></p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm font-mono">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">AMOUNT ($) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
              data-testid="payout-amount"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">METHOD *</label>
            <Dropdown
              value={method}
              onChange={setMethod}
              options={[
                { value: 'paypal', label: 'PAYPAL' },
                { value: 'other',  label: 'OTHER (SPECIFY IN NOTES)' },
              ]}
              testId="payout-method"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">NOTE (PAYPAL EMAIL / DETAILS)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.toUpperCase())}
              placeholder="e.g. PAYPAL.ME/YOURNAME"
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
              data-testid="payout-note"
            />
          </div>

          <p className="font-mono text-[10px] text-zinc-600">PAYOUTS ARE PROCESSED MANUALLY. EXPECT 1-3 BUSINESS DAYS.</p>

          <button
            onClick={submit}
            disabled={saving}
            className="w-full py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-lg transition-colors font-mono flex items-center justify-center gap-2"
            data-testid="submit-payout-req"
          >
            {saving && (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            )}
            {saving ? 'SUBMITTING...' : 'REQUEST PAYOUT'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared dropdown — same look as CustomSelect on the rest of the site
// ---------------------------------------------------------------------------
function Dropdown({ value, onChange, options, placeholder, testId, required = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const display = options.find(o => o.value === value)?.label;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-left font-mono uppercase flex items-center justify-between focus:border-teal-500 focus:outline-none"
        data-testid={testId}
      >
        <span className={display ? 'text-white' : 'text-zinc-500'}>
          {display || placeholder}{required && !display ? '' : ''}
        </span>
        <ChevronDown size={16} className={`text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 rounded-lg max-h-56 overflow-y-auto shadow-xl mt-1">
            {options.length === 0 && (
              <div className="px-3 py-2 font-mono text-xs text-zinc-500">NO OPTIONS</div>
            )}
            {options.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                className={`w-full px-3 py-2 text-left font-mono uppercase text-sm border-b border-zinc-800 last:border-0 ${
                  value === opt.value ? 'bg-teal-500/20 text-teal-400' : 'text-white hover:bg-zinc-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Per-item card on /my-consignments — handles status-driven actions
// ---------------------------------------------------------------------------
function UserConsignmentItem({ item, onAction }) {
  const { getAuthHeader } = useAuth();
  const [showCounter, setShowCounter] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const photo = (item.photos || [])[0];
  const photoUrl = photo ? (photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo) : null;

  const act = async (action, body) => {
    try {
      await axios.post(`${API}/consignment/${item.id}/${action}`, body || {}, { headers: getAuthHeader() });
      toast.success(action.replace(/-/g, ' ').toUpperCase());
      onAction();
    } catch (e) {
      toast.error((e?.response?.data?.detail || 'FAILED').toString().toUpperCase());
    }
  };

  const cancelSubmission = async () => {
    if (!window.confirm('Cancel this consignment submission?')) return;
    try {
      await axios.delete(`${API}/consignment/${item.id}`, { headers: getAuthHeader() });
      toast.success('CANCELLED');
      onAction();
    } catch (e) {
      toast.error((e?.response?.data?.detail || 'CANCEL FAILED').toString().toUpperCase());
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950 p-3 sm:p-4" data-testid={`my-cons-${item.id}`}>
      <div className="flex gap-3 sm:gap-4">
        <div className="w-14 h-14 sm:w-20 sm:h-20 bg-black border border-zinc-800 shrink-0">
          {photoUrl ? <img src={photoUrl} alt={item.title} className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-mono text-xs sm:text-sm text-white truncate uppercase">{item.title}</p>
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
            <span className="px-1.5 py-0.5 tracking-widest" style={{ background: `${STATUS_COLORS[item.status] || '#888'}22`, color: STATUS_COLORS[item.status] || '#888' }}>
              {item.status.replace(/_/g, ' ').toUpperCase()}
            </span>
            <span className="text-zinc-500">SUGG ${item.suggested_price?.toFixed(2)}</span>
            {item.final_price && <span className="text-tiffany-400">LIST ${item.final_price.toFixed(2)}</span>}
            {item.sold_price && <span className="text-purple-400">SOLD ${item.sold_price.toFixed(2)} · +${item.seller_earnings?.toFixed(2)}</span>}
            {item.seller_tracking_number && <span className="text-blue-400">TRK {item.seller_tracking_number}</span>}
          </div>
          {item.status === 'listed' && item.expires_at && <p className="font-mono text-[10px] text-zinc-500">REASSESS BY {new Date(item.expires_at).toLocaleDateString()}</p>}
          {item.admin_notes && <p className="font-mono text-[10px] text-zinc-400 italic">"{item.admin_notes}"</p>}
        </div>
      </div>

      {/* NEEDS_REVIEW actions */}
      {item.status === 'needs_review' && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
          <div className="bg-yellow-500/10 border border-yellow-500/40 p-2 font-mono text-[11px]">
            <p className="text-yellow-400 tracking-widest mb-1">UPDATED TERMS · ACTION NEEDED</p>
            <p className="text-zinc-300">
              FINAL <span className="text-tiffany-400 font-bold">${item.final_price?.toFixed(2)}</span> · COMMISSION <span className="text-white">{item.commission_pct}%</span> · FLOOR <span className="text-white">${item.base_floor?.toFixed(2)}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => act('accept-terms')} className="px-3 py-2 bg-tiffany-500 text-black font-mono text-[11px] font-bold tracking-widest hover:bg-tiffany-400" data-testid={`accept-${item.id}`}>ACCEPT TERMS</button>
            <button onClick={() => setShowCounter(true)} className="px-3 py-2 border border-yellow-500/40 text-yellow-400 font-mono text-[11px] tracking-widest hover:bg-yellow-500/10" data-testid={`counter-${item.id}`}>COUNTER</button>
            <button onClick={() => { if (window.confirm('Decline these terms and end this consignment?')) act('decline'); }} className="px-3 py-2 border border-red-500/40 text-red-400 font-mono text-[11px] tracking-widest hover:bg-red-500/10" data-testid={`decline-${item.id}`}>DECLINE</button>
          </div>
        </div>
      )}

      {/* AWAITING_SHIPMENT */}
      {item.status === 'awaiting_shipment' && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
          {item.seller_tracking_number ? (
            <div className="bg-blue-500/10 border border-blue-500/30 p-2 font-mono text-[11px]">
              <p className="text-blue-400 tracking-widest mb-1">TRACKING SUBMITTED</p>
              <p className="text-white">{item.seller_carrier} · {item.seller_tracking_number}</p>
            </div>
          ) : (
            <>
              <div className="bg-zinc-900 p-2 font-mono text-[11px]">
                <p className="text-tiffany-400 tracking-widest mb-1">SHIP YOUR ITEM TO</p>
                <p className="text-white leading-relaxed">BLK MRKT<br/>PO BOX 26<br/>SACO, ME 04072</p>
              </div>
              <button onClick={() => setShowTracking(true)} className="w-full px-3 py-2 bg-tiffany-500 text-black font-mono text-[11px] font-bold tracking-widest hover:bg-tiffany-400" data-testid={`submit-tracking-${item.id}`}>
                SUBMIT TRACKING NUMBER
              </button>
            </>
          )}
        </div>
      )}

      {/* IN_TRANSIT — tracking submitted, package en route */}
      {item.status === 'in_transit' && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
          <div className="bg-blue-500/10 border border-blue-500/30 p-2 font-mono text-[11px]">
            <p className="text-blue-300 tracking-widest mb-1">IN TRANSIT TO BLK MRKT</p>
            <p className="text-white">{item.seller_carrier || 'USPS'} · {item.seller_tracking_number}</p>
          </div>
          {item.seller_tracking_url ? (
            <a
              href={item.seller_tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-block text-center px-3 py-2 bg-blue-500 text-black font-mono text-[11px] font-bold tracking-widest hover:bg-blue-400"
              data-testid={`track-${item.id}`}
            >
              TRACK PACKAGE
            </a>
          ) : (
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent((item.seller_carrier || 'USPS') + ' ' + item.seller_tracking_number + ' tracking')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-block text-center px-3 py-2 border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 font-mono text-[11px] font-bold tracking-widest"
              data-testid={`track-${item.id}`}
            >
              TRACK PACKAGE
            </a>
          )}
        </div>
      )}

      {/* User cancel — submitted only */}
      {item.status === 'submitted' && (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex justify-end">
          <button
            onClick={cancelSubmission}
            className="px-3 py-1.5 border border-red-500/30 text-red-400/80 hover:border-red-500 hover:text-red-400 hover:bg-red-500/10 font-mono text-[11px] tracking-widest"
            data-testid={`cancel-${item.id}`}
          >
            CANCEL SUBMISSION
          </button>
        </div>
      )}

      {showCounter && <CounterOfferModal item={item} onClose={() => setShowCounter(false)} onSaved={() => { setShowCounter(false); onAction(); }} />}
      {showTracking && <TrackingSubmitModal item={item} onClose={() => setShowTracking(false)} onSaved={() => { setShowTracking(false); onAction(); }} />}
    </div>
  );
}

function CounterOfferModal({ item, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [price, setPrice] = useState(item.suggested_price || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const save = async () => {
    setError('');
    const p = parseFloat(price);
    if (!p || p <= 0) { setError('PLEASE ENTER A VALID PRICE'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/consignment/${item.id}/counter-offer`, { suggested_price: p, note }, { headers: getAuthHeader() });
      toast.success('COUNTER SENT');
      onSaved();
    } catch (e) {
      setError((e?.response?.data?.detail || 'FAILED').toString().toUpperCase());
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md">
        <div className="border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">COUNTER-OFFER</h2>
            <p className="text-xs text-zinc-500 font-mono truncate">{item.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm font-mono">{error}</div>}
          <div className="bg-zinc-800/50 border border-zinc-700 p-3 font-mono text-xs space-y-1">
            <p className="text-zinc-500">ADMIN PROPOSED <span className="text-tiffany-400">${item.final_price?.toFixed(2)}</span></p>
            <p className="text-zinc-500">COMMISSION <span className="text-white">{item.commission_pct}%</span> · FLOOR <span className="text-white">${item.base_floor?.toFixed(2)}</span></p>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">YOUR COUNTER PRICE ($) *</label>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-teal-500 focus:outline-none font-mono"
              data-testid="counter-price" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">NOTE (OPTIONAL)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value.toUpperCase())} rows={2}
              placeholder="WHY THIS PRICE?"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
              data-testid="counter-note" />
          </div>
          <button onClick={save} disabled={saving}
            className="w-full py-3 bg-tiffany-500 hover:bg-tiffany-600 disabled:bg-zinc-700 text-black font-bold rounded-lg font-mono"
            data-testid="submit-counter-btn"
          >
            {saving ? 'SENDING...' : 'SEND COUNTER-OFFER'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TrackingSubmitModal({ item, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('USPS');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const save = async () => {
    setError('');
    if (!tracking.trim()) { setError('PLEASE ENTER A TRACKING NUMBER'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/consignment/${item.id}/submit-tracking`, { tracking_number: tracking.trim(), carrier }, { headers: getAuthHeader() });
      toast.success('TRACKING SUBMITTED');
      onSaved();
    } catch (e) {
      setError((e?.response?.data?.detail || 'FAILED').toString().toUpperCase());
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md">
        <div className="border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">SUBMIT TRACKING</h2>
            <p className="text-xs text-zinc-500 font-mono truncate">{item.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm font-mono">{error}</div>}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">TRACKING NUMBER *</label>
            <input type="text" value={tracking} onChange={(e) => setTracking(e.target.value.toUpperCase())}
              placeholder="9400 ..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono uppercase"
              data-testid="tracking-input" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">CARRIER *</label>
            <Dropdown
              value={carrier}
              onChange={setCarrier}
              options={[
                { value: 'USPS',  label: 'USPS' },
                { value: 'UPS',   label: 'UPS' },
                { value: 'FEDEX', label: 'FEDEX' },
                { value: 'DHL',   label: 'DHL' },
                { value: 'OTHER', label: 'OTHER' },
              ]}
              testId="tracking-carrier"
            />
          </div>
          <button onClick={save} disabled={saving}
            className="w-full py-3 bg-tiffany-500 hover:bg-tiffany-600 disabled:bg-zinc-700 text-black font-bold rounded-lg font-mono"
            data-testid="submit-tracking-btn"
          >
            {saving ? 'SUBMITTING...' : 'SUBMIT TRACKING'}
          </button>
        </div>
      </div>
    </div>
  );
}
